import { Box, CircularProgress, Typography } from '@mui/material';
import {
  DEFAULT_PRICE_DECIMAL_SEPARATOR,
  Languages,
} from '@/common/utils/constants';
import { Maybe, Resource as ResourceType } from '@/generated/contentfulTypes';
import {
  styledActualTermPriceWithPeriod,
  styledPeriodText,
  styledPriceBox,
  styledPriceWrapper,
  stylesCrossedOutPrice,
} from './PriceSection.styled';

import AnimatedPrice from './AnimatedPrice';
import { Breakpoints } from '@/common/theme/constants';
import { QUERY_KEYS } from '@/common/types/react-query-keys.interfaces';
import Resource from '@/common/components/Resource/Resource';
import { formatPrice } from '@/common/utils/formatPriceForDisplay';
import getSettings from '@/common/utils/getSettings';
import { useMediaQuery } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

interface PriceSectionProps {
  price?: Maybe<number>;
  period?: Maybe<ResourceType>;
  additionalSubtext?: Maybe<ResourceType>;
  lang: Languages;
  discountedPrice?: string;
  isFetchingPrice?: boolean;
}
export default function PriceSection({
  price,
  period,
  additionalSubtext,
  lang,
  discountedPrice,
  isFetchingPrice,
}: PriceSectionProps) {
  const { data: settings } = useQuery({
    queryKey: [QUERY_KEYS.SETTINGS, lang],
    queryFn: () => getSettings(lang),
  });

  const isMobile = useMediaQuery(`(max-width:${Breakpoints.sm}px)`);
  const sep =
    settings?.priceDecimalSeparator ?? DEFAULT_PRICE_DECIMAL_SEPARATOR;

  const displayPrice = discountedPrice ?? formatPrice(price ?? 0, sep);
  const priceValue = parseFloat(
    String(displayPrice)
      .replace(sep, '.')
      .replace(/[^0-9.-]/g, '')
  );
  const isWholeNumber = Number.isInteger(priceValue);
  const formattedOriginalPrice = price != null ? formatPrice(price, sep) : '';

  if (isFetchingPrice) {
    return <CircularProgress size="16px" />;
}

  return (
    <Box sx={styledPriceBox}>
      <Box sx={styledPriceWrapper}>
        {discountedPrice && (
          <Typography variant="h10" sx={stylesCrossedOutPrice}>
            {settings?.currency}
            {formattedOriginalPrice}
            {isMobile && period && isWholeNumber && ',-'}
          </Typography>
        )}
        <Box sx={styledActualTermPriceWithPeriod}>
          <AnimatedPrice
            price={displayPrice}
            currency={settings?.currency ?? undefined}
            variant="h10"
            fontWeight={400}
          />
        </Box>
      </Box>
      {period && (
        <Typography sx={styledPeriodText} variant="body5">
          <Resource {...period} />
        </Typography>
      )}
      {additionalSubtext && (
        <Box>
          <Typography variant="body5">
            <Resource {...additionalSubtext} />
          </Typography>
        </Box>
      )}
    </Box>
  );
}
